/**
 * pdf.js, loaded ON DEMAND and shared by everything that draws a PDF page in
 * the admin — the placement editor and the document composer's page picker.
 *
 * Not imported at module scope anywhere. It is a large engine that only the
 * handful of people arranging a template ever need, so nobody else should
 * download it — and a module-scope import would put it in the graph of every
 * page reachable from `App`, which made every platform-console test load it
 * and die on `Iterator is not defined` (pdf.js 6 wants a newer runtime than
 * the suite's).
 */
export type PdfJs = typeof import('pdfjs-dist');

let pending: Promise<PdfJs> | null = null;

export function loadPdfJs(): Promise<PdfJs> {
  if (!pending) {
    pending = import('pdfjs-dist').then((mod) => {
      // The worker ships with the package; Vite needs the URL rather than a
      // bare specifier so it is bundled and versioned with its library.
      mod.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.mjs', import.meta.url
      ).toString();
      return mod;
    });
  }
  return pending;
}

/**
 * Every page of a PDF as a small image, for a page picker.
 *
 * Rendered at a fixed small width rather than a fixed scale, so a Letter page
 * and an A4 one make thumbnails of the same size. Failures come back as an
 * empty string per page rather than throwing: a picker with a blank tile is
 * usable, a picker that never appears is not.
 */
export async function pageThumbnails(url: string, width = 120, timeoutMs = 20_000): Promise<string[]> {
  // Bounded. A worker that never answers — a dev server serving it slowly, a
  // blocked script — would otherwise leave the picker saying "loading…" for
  // as long as the panel is open. The picker works without pictures; it
  // should not wait forever for them.
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('thumbnails timed out')), timeoutMs));
  const pdfjs = await loadPdfJs();
  const task = pdfjs.getDocument({ url });
  const doc = await Promise.race([task.promise, timeout]);
  try {
    return await thumbnailsOf(doc, width, timeout);
  } finally {
    // The worker holds the whole document until told otherwise; a picker
    // opened a dozen times would otherwise hold a dozen.
    await task.destroy().catch(() => undefined);
  }
}

async function thumbnailsOf(
  doc: Awaited<ReturnType<PdfJs['getDocument']>['promise']>, width: number, timeout: Promise<never>
): Promise<string[]> {
  const out: string[] = [];
  for (let i = 1; i <= doc.numPages; i += 1) {
    try {
      const page = await doc.getPage(i);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: width / base.width });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) { out.push(''); continue; }
      // `print` intent, deliberately. Display-intent rendering is paced on
      // requestAnimationFrame, which a hidden tab never fires — so a picker
      // opened and then switched away from would sit on "loading…" until the
      // tab came back. A thumbnail is a static picture; print appearance is
      // exactly right for it, and it draws whether or not the tab is visible.
      await Promise.race([page.render({ canvas, canvasContext: ctx, viewport, intent: 'print' }).promise, timeout]);
      out.push(canvas.toDataURL('image/png'));
    } catch {
      out.push('');
    }
  }
  return out;
}
